"use client";

import React, { use } from "react";
import Navbar from "@/components/Navbar";
import Footer2 from "@/components/landing-page/Footer2";
import { ArrowLeft, ArrowRight, Calendar, Clock, Share2, Linkedin, Twitter, Facebook, MessageCircle } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import ReactMarkdown from "react-markdown";

// Mock data for the posts
const postsData: Record<string, any> = {
  "5-tasks-every-business-still-doing-manually": {
    title: "The 5 Tasks Every Business Is Still Doing Manually That Take 11 Minutes to Automate",
    excerpt: "There is a specific kind of waste that never shows up on a balance sheet. It does not look like a problem. It looks like work.",
    date: "April 28, 2026",
    readTime: "8 min read",
    category: "Operations",
    author: "Senera Core",
    image: "/blog/5-tasks-none-need-human.png",
    content: `There is a specific kind of waste that never shows up on a balance sheet.

It does not look like a problem. It looks like work. Someone on the team doing their job, handling things, keeping operations moving. Except what they are handling has not required a human being for at least three years. The tools to replace it have existed the entire time. Nobody just sat down and built them.

The average operations team spends **14 hours every week** on tasks a properly built workflow handles in milliseconds. Not complex tasks. Not creative tasks. Repeatable, never-changing, entirely predictable tasks that happen on the same schedule every single week without exception.

Every automation below takes under 11 minutes to build. Not because they are trivial. Because the planning is already done. Read this once, then go build the one that is costing your team the most hours.

### 1. New Lead Intake and CRM Entry

Every time a lead comes in through a form, an email, a LinkedIn DM, or an ad platform, someone on the team manually copies the contact details, pastes them into the CRM, assigns a rep, and sends a first-touch email.

The reason it is still manual at most companies has nothing to do with capability. The form, the CRM, and the email tool are three separate platforms and most teams have never gotten around to connecting them. So someone in marketing or sales ops does it by hand, every single time, for every single lead.

If the team handles 40 leads a week at 4 minutes each, that is **2.7 hours per week** spent on copy-paste. Every week. For years.

The automation has four nodes. A trigger fires when a new form submission comes in. The first action creates a contact in the CRM. The second assigns a rep based on lead source or company size. The third sends a first-touch email directly from the rep's account. The whole workflow runs in under 3 seconds every time a lead arrives.

**Build time: 7 minutes.**

### 2. Monthly Invoice Follow-Ups

When an invoice goes unpaid past its due date, someone opens the accounting tool, checks who owes what, drafts a reminder email, sends it, and logs that the follow-up happened. Then they do it again next month. And the month after that.

A 50-client agency following up on 20% of invoices monthly burns **3 hours on this every single month**. That is 36 hours a year chasing money that a workflow would have collected automatically.

The automation runs on a scheduled trigger at 9 AM daily. It queries the accounting tool for invoices that are 7 days past due, filters out anything already followed up within the last 3 days, sends a templated email from the finance address, and logs the follow-up in the CRM.

The filter step is the one people skip and then regret. Without it, clients receive duplicate emails within days of each other and call to complain. **Build the filter first.**

**Build time: 9 minutes.**

### 3. New Client Onboarding

When a client signs a contract, a sequence of six things needs to happen. Welcome email. Intake form request. Slack channel creation. Notion workspace setup. First call booking. Internal handoff to the delivery team.

At 45 minutes per new client and 8 clients per month, that is **6 hours of setup work every single month**. For work that is completely identical every time. For work that has never changed once.

The automation triggers when a contract is signed via DocuSign or PandaDoc. It creates a Slack channel named after the client, sends the welcome email with an intake form link, duplicates a Notion workspace from a master template, schedules the onboarding call through Calendly, and sends a Slack message to the delivery team with all client details attached.

**Build time: 11 minutes.**

### 4. Weekly Performance Reporting

Every Monday, someone pulls data from three to five different tools. Google Analytics. The CRM. Stripe. The ad platform. They paste everything into a spreadsheet or a Google Slides deck, format it to match the template, and send it to the client or to leadership.

Two hours every Monday. Fifty-two Mondays a year. That is **104 hours of report-building annually** for data that is entirely capable of pulling itself.

The automation triggers on Monday at 8 AM. It fetches data from the analytics, CRM, and Stripe APIs simultaneously. A code node calculates week-over-week changes and flags anything down more than 10%. The results populate a Google Sheets template automatically. A Slack message or email goes out with the report link attached.

**Build time: 10 minutes.**

### 5. Support Ticket Triage

When a support ticket comes in through email or Intercom, someone reads it, categorises it as billing, technical, or account-related, assigns it to the right person, and sends an acknowledgement email to the customer.

The reason teams assume this one cannot be automated is that routing requires judgment. Except **80% of tickets fall into the same three or four categories** and the routing rules are almost always identical.

The automation triggers when a new ticket arrives. An AI classification node reads the ticket and categorises it in under one second. The workflow routes it to the correct team member based on the category. An acknowledgement email goes to the customer immediately. The ticket gets logged for tracking.

The AI node gets the category right approximately 85% of the time. Build a fallback for anything categorised as "other" that routes to a shared inbox for human review.

**Build time: 8 minutes.**

---

None of these automations are technically complicated. Every one of them is a trigger, two to five actions, and a filter or a conditional.

The reason they stay manual is not technical at all. Nobody has two uninterrupted hours to sit down, plan the whole thing, and build it cleanly. Everyone has 11 minutes between meetings and no clear picture of where to start.

**The bottleneck is never the tools. It is always the planning.**

If any of these five are still running manually in your business, pick one this week. Take 11 minutes. Write the logic out in plain English first. Then build it. It will be running before lunch.
    `
  },
  "ai-reshaping-enterprise-automation-2026": {
    title: "How AI is Reshaping Enterprise Automation in 2026",
    excerpt: "The days of simple rule-based automation are over. Learn how cognitive agents are taking over complex workflows.",
    date: "April 19, 2026",
    readTime: "5 min read",
    category: "AI Trends",
    author: "Senera Core",
    image: "/blog/ai-future.png",
    content: `
The enterprise landscape is undergoing a massive paradigm shift. Just a few years ago, RPA (Robotic Process Automation) was the gold standard for operational efficiency. It relied on rigid, rule-based systems that broke down the moment a website changed its layout or an invoice arrived in a slightly different format.

Fast forward to 2026, and the narrative has shifted from *Robotic* Process Automation to *Cognitive* Process Automation. 

### The Shift to Cognitive Agents

Modern AI agents don't just follow paths; they understand intent. They navigate ambiguous interfaces, synthesize chaotic data sets into structured insights, and make deterministic choices based on probabilistic models. The image above—a driver made of pure logic and code navigating the horizon—perfectly encapsulates this era. We are no longer just building engines; we are building the drivers.

When implementing cognitive agents, the traditional barriers to entry (rigid APIs, perfect data hygiene) are significantly lowered. Generative vision models can read UIs like humans do, and language models can negotiate APIs by simply reading the documentation on the fly.

### Why This Matters for Your Bottom Line

1. **Resilience over Rigidity:** When a third-party vendor updates their platform, legacy automations break. Cognitive agents adapt in real-time, reducing maintenance overhead by up to 80%.
2. **Contextual Decision Making:** Need to triage customer support tickets based on sentiment and urgency? Modern agents handle this without needing explicitly programmed rules for every edge case.
3. **Infinite Scale:** A cognitive agent can perform the work of a junior analyst 24/7 without fatigue, allowing your human talent to focus exclusively on high-leverage strategy.

The future belongs to the businesses that stop doing things the hard way and embrace intelligent, autonomous infrastructure. At Senera Core, we're building exactly that.
    `
  },
  "building-resilient-ai-systems-build-vs-buy": {
    title: "Building Resilient AI Systems: Build vs Buy",
    excerpt: "When should you build your own AI infrastructure, and when should you rely on off-the-shelf tools?",
    date: "April 18, 2026",
    readTime: "7 min read",
    category: "Engineering",
    author: "Senera Core",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    content: "The build versus buy debate in AI infrastructure is more complex than ever. While off-the-shelf APIs offer fast time-to-market, custom-trained models provide defensible moats. We explore the engineering tradeoffs and strategic implications for enterprise teams..."
  },
  "true-cost-of-manual-data-entry": {
    title: "The True Cost of Manual Data Entry",
    excerpt: "Unpacking the hidden financial and operational costs of relying on humans for repetitive tasks.",
    date: "April 10, 2026",
    readTime: "4 min read",
    category: "Operations",
    author: "Senera Core",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    content: "Manual data entry isn't just a waste of time; it's a silent killer of morale and operational accuracy. We break down the true cost of human error and fatigue, and how Task Blocks can eliminate this bottleneck entirely..."
  }
};

const ShareButtons = ({ post }: { post: any }) => {
  const handleShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`${post.title}\n\n${window.location.href}\n\nRead more at Senera Core!`);
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
        break;
      case 'native':
        if (navigator.share) {
          navigator.share({
            title: post.title,
            text: `${post.title}\n\nRead more at Senera Core!`,
            url: window.location.href,
          }).catch(console.error);
        } else {
          navigator.clipboard.writeText(`${post.title}\n\n${window.location.href}\n\nRead more at Senera Core!`);
          alert('Link copied to clipboard!');
        }
        break;
    }
  };

  return (
    <div className="flex gap-3 flex-wrap">
      <button onClick={() => handleShare('twitter')} className="w-10 h-10 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors" title="Share on X / Twitter">
        <svg viewBox="0 0 24 24" aria-hidden="true" className="w-3.5 h-3.5 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>
      </button>
      <button onClick={() => handleShare('linkedin')} className="w-10 h-10 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors" title="Share on LinkedIn">
        <Linkedin className="w-4 h-4" />
      </button>
      <button onClick={() => handleShare('facebook')} className="w-10 h-10 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors" title="Share on Facebook">
        <Facebook className="w-4 h-4" />
      </button>
      <button onClick={() => handleShare('whatsapp')} className="w-10 h-10 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors" title="Share on WhatsApp">
        <MessageCircle className="w-4 h-4" />
      </button>
      <button onClick={() => handleShare('native')} className="w-10 h-10 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors" title="Share Article">
        <Share2 className="w-4 h-4" />
      </button>
    </div>
  );
};

export default function BlogPost({ params }: { params: Promise<{ slug: string }> }) {
  const resolvedParams = use(params);
  const post = postsData[resolvedParams.slug];

  if (!post) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center flex-col text-black">
        <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
        <Link href="/blog" className="text-indigo-600 hover:underline">
          &larr; Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 text-black selection:bg-indigo-100 flex flex-col">
      <Navbar isLoggedIn={false} />

      <main className="pt-32 pb-24 flex-grow">
        <article className="max-w-4xl mx-auto px-6">
          <Link href="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-black transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>

          <header className="mb-10 text-center md:text-left">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs font-mono text-zinc-500 mb-6 uppercase tracking-wider">
              <span className="text-indigo-600 font-semibold bg-indigo-50 px-3 py-1 rounded-full">{post.category}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {post.date}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-black leading-[1.1] mb-6">
              {post.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-zinc-600 max-w-3xl leading-relaxed font-light mx-auto md:mx-0">
              {post.excerpt}
            </p>
          </header>

          <div className="relative w-full aspect-[21/9] rounded-2xl overflow-hidden mb-12 shadow-sm border border-zinc-200">
            <Image 
              src={post.image} 
              alt={post.title} 
              fill 
              className="object-cover"
              priority
              unoptimized={post.image.startsWith('http')}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-12">
            {/* Sidebar with author and share */}
            <aside className="md:w-1/4 flex-shrink-0">
              <div className="sticky top-32 space-y-8">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Written by</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-white font-bold text-sm">
                      SC
                    </div>
                    <div>
                      <p className="font-semibold text-black">{post.author}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Share</p>
                  <ShareButtons post={post} />
                </div>
              </div>
            </aside>

            {/* Article Content */}
            <div className="md:w-3/4">
              <div className="prose prose-zinc prose-lg max-w-none font-serif">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </div>
              
              <div className="mt-16 pt-8 border-t border-zinc-200">
                <div className="bg-zinc-100 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h4 className="text-xl font-bold text-black mb-2">Ready to automate your workflows?</h4>
                    <p className="text-zinc-600">Let's discuss how Senera Core can transform your enterprise.</p>
                  </div>
                  <Link href="/contact" className="whitespace-nowrap inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-100">
                    Book a Strategy Call <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer2 />
    </div>
  );
}
